import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Game} from "../Game";
import {SymbolContainer} from "../reels/SymbolContainer";
import {gsap} from "gsap";
import {GameUtils} from "../utils/GameUtils";
import {Config} from "../config/Config";
import {Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {BitMapMultiplier} from "../components/BitmapMultiplier";
import {GameAssets} from "../GameAssets";
import {Sound} from "../sound/Sound";
import {XHoleSymbol} from "../reels/XHoleSymbol";

export class EffectsView extends PIXI.Container implements StageLayer {

    constructor() {
        super();
        this.name = "EffectsView";
        this.init();
    }


    public reset() {
        this.removeChildren();
    }

    public playAndHidePlusTextAnimation(): gsap.core.Timeline {
        let plusValue: number = 0;
        let plusTextArr: BitMapMultiplier[] = [];
        let blastArr: TimelineSprite[] = [];
        let scatterSymArr: SymbolContainer[] = [];
        const tl: gsap.core.Timeline = gsap.timeline();
        for (let reelId: number = 0; reelId < 5; reelId++) {
            if (Game.model.scatterLandedPos[reelId] > -1) {
                if (Game.model.gameData.fireFrames[reelId][Game.model.scatterLandedPos[reelId]]) {
                    plusValue = 1;
                } else if (Game.model.gameData.fireReels[reelId]) {
                    plusValue = 2;
                } else {
                    continue;
                }

                const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, Game.model.scatterLandedPos[reelId]);
                scatterSymArr.push(symbolContainer);
                const posX: number = symbolContainer.position.x + symbolContainer.parent.position.x;
                let posY: number = symbolContainer.position.y + symbolContainer.parent.position.y + 215;
                if (symbolContainer.isTopHalf) {
                    posY += 49.5;
                }
                const plusText: BitMapMultiplier = this.createPlusText(plusValue);
                plusText.position.set(posX, 360);
                this.addChild(plusText);
                plusTextArr.push(plusText);
                const blast: TimelineSprite = this.createBlastTimelineSprite();

                blast.position.set(posX, posY);
                this.addChild(blast);
                blastArr.push(blast);

                tl.add(gsap.to(plusText, {x: posX, y: posY + 10, duration: 0.2}), 0);
            }
        }

        plusTextArr.forEach((plusText: BitMapMultiplier, index: number) => {
            tl.add(() => {
                blastArr[index].visible = true;
                blastArr[index].alpha = 1;
            }, 0);
            tl.to(plusText, {duration: 0.05, alpha: 1}, 0)
            tl.fromTo(plusText.scale, {x: 4, y: 4}, {x: 0.5, y: 0.5, duration: 0.2}, 0);
            tl.add(() => {
                blastArr[index].getAnimationAutoShowHide(true, true)
            }, 0.15);

            //scatter symbol shake
            tl.to(scatterSymArr[index].scale, {x: 0.9, y: 0.9, duration: 0.05}, 0.15);
            tl.to(scatterSymArr[index].scale, {x: 1.1, y: 1.1, duration: 0.1}, ">");
            tl.to(scatterSymArr[index].scale, {x: 1, y: 1, duration: 0.1}, ">");

            tl.to(plusText.scale, {x: 0.7, y: 0.7, duration: 0.1}, 0.2);
            tl.to(plusText.scale, {x: 0.55, y: 0.55, duration: 0.1}, ">");

            tl.add(() => {
                gsap.to(plusText, {
                    alpha: 0, duration: 2, onComplete: () => {
                        plusText.destroy();
                    }
                })
            }, ">");
        })
        tl.add(() => {
            blastArr.forEach((sprite: TimelineSprite) => {
                sprite.destroy();
            })
        })
        return tl;
    }

    public playFlashEffectForXHole(): gsap.core.Timeline {
        const symbolContainer: SymbolContainer = GameUtils.getSymbol(2, 1);
        const glow: PIXI.Sprite = this.createFlashEffect(symbolContainer);
        glow.position.y += symbolContainer.symbols[0].position.y;
        if (symbolContainer.symbols[0].currentHeight) {
            glow.height = symbolContainer.symbols[0].currentHeight * 2;
        }
        this.addChild(glow);
        const flashTl: gsap.core.Timeline = gsap.timeline();
        flashTl.add(() => {
            Game.soundPlayer.playSound(Sound.S1_ACTIVATION);
        });
        flashTl.to(glow, {alpha: 1, duration: 0.1666}, 0);
        flashTl.to(glow, {alpha: 0, duration: 0.1666});
        flashTl.add(() => {
            glow.parent?.removeChild(glow);
        });
        return flashTl
    }

    public playAndHideFlashEffect(symbol: string, targetHeight?: number): gsap.core.Timeline {
        let flashArr: PIXI.Sprite[] = [];
        switch (symbol) {
            case SYMBOL.S1:
                for (let reelID: number = 0; reelID < 5; reelID++) {
                    if (Game.model.scatterLandedPos[reelID] > -1) {
                        const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelID, Game.model.scatterLandedPos[reelID]);
                        const glow: PIXI.Sprite = this.createFlashEffect(symbolContainer);
                        this.addChild(glow);
                        flashArr.push(glow);
                    }
                }
                break;

            case SYMBOL.X_HOLE:
                const symbolContainer: SymbolContainer = GameUtils.getSymbol(2, 1);
                const glow: PIXI.Sprite = this.createFlashEffect(symbolContainer);
                this.addChild(glow);
                if (targetHeight) {
                    glow.height = targetHeight * 2;
                }
                flashArr.push(glow);
                break;
        }

        const flashTl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                flashArr.forEach((sprite: PIXI.Sprite) => {
                    sprite.destroy();
                })
                flashArr = [];
            }
        });
        if (flashArr.length) {
            flashTl.add(() => {
                Game.soundPlayer.playSound(Sound.S1_ACTIVATION);
            });
        }
        flashArr.forEach((sprite: PIXI.Sprite) => {
            flashTl.to(sprite, {alpha: 1, duration: 0.1666}, 0);
            flashTl.to(sprite, {alpha: 0, duration: 0.1666}, ">");
        });

        return flashTl;
    }

    public xHoleLandingAnimation(rowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const xHoleLandBlast: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("xHoleLandEffect"));
        xHoleLandBlast.position.set(Config.SYMBOL_WIDTH * 2.5, Config.SYMBOL_HEIGHT * rowId + 117);
        this.addChild(xHoleLandBlast);
        tl.add(GameUtils.createTimelineForAnimation(xHoleLandBlast, 0, "land", false), 0);
        tl.add(GameUtils.shake(0, 10, 60, 1, 0.5), 0)
        tl.add(() => {
            GameUtils.resetSpineItem(xHoleLandBlast);
            xHoleLandBlast.destroy();
            this.removeChild(xHoleLandBlast);
        })
        return tl;
    }

    public playXHoleLandAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const xHoleRowId: number = Game.model.gameData.reels[2].indexOf(SYMBOL.X_HOLE);
        if (xHoleRowId > -1) {
            const xHoleSym: SymbolContainer = GameUtils.getSymbol(2, xHoleRowId);
            if (Game.model.isXHoleActive()) {
                Game.reelView.reels[2].addChild(xHoleSym);
                tl.add(this.xHoleLandingAnimation(xHoleRowId), 0);
                tl.add(() => {
                    Game.foregroundElements.featureLogoContainer.playXHoleActive();
                    (xHoleSym.symbols[0] as XHoleSymbol).playAnticipation();
                }, 0);
            } else {
                tl.add((xHoleSym.symbols[0] as XHoleSymbol).playNoWin());
            }
        }
        return tl;
    }

    public stopXHoleLandAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const xHoleRowId: number = Game.model.gameData.reels[2].indexOf(SYMBOL.X_HOLE);
        if (xHoleRowId > -1) {
            const xHoleSym: SymbolContainer = GameUtils.getSymbol(2, xHoleRowId);
            tl.add(() => {
                (xHoleSym.symbols[0] as XHoleSymbol).stopAnticipation();
            });
        }
        return tl;
    }

    public createScatterAnticipationEffect(height: number): TimelineSprite {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("scatter_symbol_anticipation/scatter_symbol_anticipation_000"));
        item.anchor.set(0.5);
        item.width = 180;
        item.height = height * 1.26;
        item.blendMode = PIXI.BLEND_MODES.ADD;
        this.addChild(item);
        return item;
    }

    public stopScatterAnticipationEffect() {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                Game.anticipationView.scatterSymArr = [];
            }
        });

        Game.anticipationView.scatterSymArr.forEach((effect: TimelineSprite) => {
            tl.add(() => {
                effect.stopLoop();
                effect.parent?.removeChild(effect);
            })
        })
    }

    private createBlastTimelineSprite(): TimelineSprite {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("multiplier_blast/multiplier_blast_"));
        item.hide();
        item.anchor.set(0.5);
        item.scale.set(3);
        item.blendMode = PIXI.BLEND_MODES.ADD;
        return item;
    }

    private createPlusText(value: number) {
        const plusText: BitMapMultiplier = new BitMapMultiplier(GameAssets.BITMAP_FONT_X_MULTIPLIER_WIN_COUNT_UP, 30, "+");
        plusText.pivot.set(0, plusText.height / 2);
        plusText.value = value;
        plusText.alpha = 0;
        return plusText;
    }

    private createFlashEffect(symbolContainer: SymbolContainer): PIXI.Sprite {
        const scatterWinAnim: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures("win_shine/shine_2_00030")[0]);
        scatterWinAnim.anchor.set(0.5);
        scatterWinAnim.scale.set(1.8);
        scatterWinAnim.alpha = 0;
        scatterWinAnim.position.set(symbolContainer.position.x + symbolContainer.parent.position.x, symbolContainer.position.y + symbolContainer.parent.position.y + 215);

        if (symbolContainer.isTopHalf) {
            scatterWinAnim.scale.y = 0.9;
            scatterWinAnim.position.y += 49.5;
        }
        return scatterWinAnim;
    }

    private init() {
        this.position.set(0, Config.MARGIN_TOP);
    }

}